import type { Triple } from '@/lib/map-field-errors';
import type { InstrumentRunDataRow, InstrumentRunForm } from '@/types';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import type { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import runStyles from '../../styles.module.css';
import { defaultLibraryGroupForIndex } from '../RunEditForm/assaySelection';

import type { CustomerPlateLayout } from './helpers';
import { createEmptyRows, sampleManagerReducer } from './reducer';
import SampleTable from './SampleTable';

// Helper to compare two sample arrays by the key fields used in sync logic
// Placed at module scope so it's stable and not recreated on every render.
const areSamplesEqual = (a?: InstrumentRunDataRow[] | null, b?: InstrumentRunDataRow[] | null): boolean => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  return a.every((s, i) => {
    const e = b[i];
    return (
      s.name === e?.name &&
      s.org_name === e?.org_name &&
      s.study_name === e?.study_name &&
      s.well_position === e?.well_position &&
      s.demux_index === e?.demux_index &&
      s.is_control === e?.is_control &&
      s.sample_type === e?.sample_type
    );
  });
};

const SampleManager: FC<{
  form: InstrumentRunForm;
  noOfSamples: number;
  showCellErrors?: boolean;
  readOnlyColumns?: string[];
  showCopyToRows?: boolean;
  serverErrors?: Triple[];
  /** Plate coordinates for customer rows, derived from the selected assay's control layout.
   *  Undefined when no assay is selected or its control layout is unusable — rows then fall back
   *  to the legacy A1-based coordinates. */
  customerLayout?: CustomerPlateLayout;
  /** Fired when a single row is edited — lets the parent drop that row's stale server error. */
  onSampleRowEdit?: (rowIndex: number) => void;
  /** Fired when a bulk edit touches every row — lets the parent drop all stale server errors. */
  onSampleRowsBulkEdit?: () => void;
  /** The library groups active for the current libraryGroupCount selection, from RunEditForm's
   *  activeLibraryGroupNames memo. Empty means the run config hasn't loaded yet. */
  activeLibraryGroups?: string[];
}> = ({
  form,
  noOfSamples,
  showCellErrors = false,
  readOnlyColumns = [],
  showCopyToRows = false,
  serverErrors,
  customerLayout,
  onSampleRowEdit,
  onSampleRowsBulkEdit,
  activeLibraryGroups = [],
}) => {
  const { t } = useTranslation('sampleManager');
  const { t: tHeader } = useTranslation('runs', { keyPrefix: 'header' });

  const initialState = useMemo(
    () => ({
      rowData:
        form.state?.values?.samples && form.state.values.samples.length > 0
          ? form.state.values.samples
          : createEmptyRows(noOfSamples, customerLayout),
      cellErrors: {},
      libraryGroupWarning: null,
    }),
    // Seeds useReducer's initial state only — React ignores it after the first render, so a
    // customerLayout that arrives later reaches existing rows via the RESIZE_ROWS effect below.
    [noOfSamples, customerLayout, form.state?.values?.samples],
  );

  const [state, dispatch] = useReducer(sampleManagerReducer, initialState);
  const gridRef = useRef<AgGridReact<InstrumentRunDataRow>>(null);

  useEffect(() => {
    if (serverErrors === undefined || serverErrors === null) return;
    if (serverErrors.length === 0) {
      dispatch({ type: 'CLEAR_CELL_ERRORS' });
      return;
    }
    dispatch({ type: 'SET_CELL_ERRORS', payload: { triples: serverErrors } });
    const first = serverErrors[0];
    gridRef.current?.api?.ensureIndexVisible(first.row, 'middle');
    gridRef.current?.api?.refreshCells({ force: true });
  }, [serverErrors]);

  const isUserEditingRef = useRef(false);
  // RESIZE_ROWS updates rowData immediately but form.setFieldValue is async; this stops the
  // form-sync effect overwriting freshly-resized rows with the stale form samples.
  const isResizingRef = useRef(false);
  // Holds the expected samples array after a resize, for deterministic clearing
  const expectedResizedSamplesRef = useRef<InstrumentRunDataRow[] | null>(null);
  // Holds the expected samples array after an edit, for deterministic clearing
  const expectedEditedSamplesRef = useRef<InstrumentRunDataRow[] | null>(null);
  // Always holds the latest rowData so the noOfSamples effect can read it
  // without needing state.rowData in its dependency array (which would cause
  // the effect to re-fire on every cell edit).
  const rowDataRef = useRef(state.rowData);
  useEffect(() => {
    rowDataRef.current = state.rowData;
  }, [state.rowData]);

  // Sync form samples to SampleManager state when they differ
  // This ensures draft data and any external updates to form.samples are reflected in the table
  // CRITICAL: This effect must run whenever form data changes, including after form.reset()
  useEffect(() => {
    const formSamples = form.state?.values?.samples;

    // Skip if no form samples
    if (!formSamples) {
      return;
    }

    // If editing an existing run, allow syncing even if formSamples is empty (to clear table)
    // If creating, let UPDATE_ROW_COUNT handle empty state
    if (formSamples.length === 0 && !form.state?.values?.name) {
      return;
    }

    // Skip if user is actively editing to prevent disrupting their workflow
    if (isUserEditingRef.current) {
      return;
    }

    // Skip if a resize is in-flight — the noOfSamples effect is responsible for
    // updating both the reducer state and the form value together; letting the
    // sync effect run in between would revert state.rowData back to the old rows.
    if (isResizingRef.current) {
      return;
    }

    // Check if form samples differ from current state
    const samplesAreDifferent =
      formSamples.length !== state.rowData.length ||
      formSamples.some((sample, index) => {
        const currentRow = state.rowData[index];
        if (!currentRow) return true;

        return (
          sample.name !== currentRow.name ||
          sample.org_name !== currentRow.org_name ||
          sample.study_name !== currentRow.study_name ||
          sample.well_position !== currentRow.well_position ||
          sample.demux_index !== currentRow.demux_index ||
          sample.is_control !== currentRow.is_control ||
          sample.sample_type !== currentRow.sample_type
        );
      });

    // Sync if samples are different.
    if (samplesAreDifferent) {
      dispatch({ type: 'SYNC_FROM_FORM', payload: { rowData: formSamples } });
    }
  }, [form.state?.values?.samples, state.rowData, form]);

  // Re-lays rows when noOfSamples or the assay's customerLayout changes, preserving entered data.
  // A late-arriving customerLayout is what repositions rows once the run config loads.
  // rowDataRef is used instead of state.rowData so cell edits don't re-fire this.
  useEffect(() => {
    // Mark as resizing so the form-sync effect does not clobber the new rows
    // before form.setFieldValue has propagated through TanStack Form's state machine.
    isResizingRef.current = true;

    const existingRows = rowDataRef.current;
    dispatch({
      type: 'RESIZE_ROWS',
      payload: { count: noOfSamples, existingRows, layout: customerLayout },
    });

    // Mirror what the reducer does so the form value stays in sync
    const allGeneratedRows = createEmptyRows(noOfSamples, customerLayout);
    const mergedRows = allGeneratedRows.map((generatedRow, i) =>
      i < existingRows.length
        ? { ...existingRows[i], well_position: generatedRow.well_position, demux_index: generatedRow.demux_index }
        : generatedRow,
    );
    form.setFieldValue?.('samples', mergedRows);

    // Store the expected samples for deterministic clearing
    expectedResizedSamplesRef.current = mergedRows;

    // Update synchronously (not just via its own effect) so the library-group effect below
    // sees this resize's result if both fire in the same commit.
    rowDataRef.current = mergedRows;
  }, [noOfSamples, customerLayout, form]);

  // Declared AFTER the resize effect so this dispatch lands on top of RESIZE_ROWS's result when
  // both fire in the same commit (a real race, caught by the local-backend Playwright run).
  // Gated on showCellErrors so a fresh create form's placeholders don't flag red on mount.
  useEffect(() => {
    dispatch({ type: 'SET_LIBRARY_GROUPS', payload: { activeGroups: activeLibraryGroups, validate: showCellErrors } });
    // SET_LIBRARY_GROUPS only touches reducer state — mirror its auto-fill into the form so an
    // untouched row still submits with a group (POR-413: resized multi-group rows shipped blank).
    if (activeLibraryGroups.length >= 1) {
      const rows = rowDataRef.current;
      const filledRows = rows.map((row, i) =>
        activeLibraryGroups.length === 1
          ? { ...row, library_group: activeLibraryGroups[0] ?? null }
          : row.library_group
            ? row
            : { ...row, library_group: defaultLibraryGroupForIndex(i, rows.length, activeLibraryGroups) },
      );
      form.setFieldValue?.('samples', filledRows);
    }
  }, [activeLibraryGroups, showCellErrors, form]);

  const handleRowDataChange = useCallback(
    (rowData: InstrumentRunDataRow[]) => {
      // Mark that user is editing to prevent sync disruption
      isUserEditingRef.current = true;

      // Keep rowDataRef current so that if noOfSamples changes while the user
      // is editing, RESIZE_ROWS gets the latest data (not stale empty rows).
      rowDataRef.current = rowData;

      // Update form state when user edits cells inline
      form.setFieldValue?.('samples', rowData);

      // Store the expected samples for deterministic clearing
      expectedEditedSamplesRef.current = rowData;

      // An edit is exactly the "user has touched this" signal showCellErrors exists to wait for,
      // so always re-validate here regardless of that gate.
      dispatch({ type: 'SET_LIBRARY_GROUPS', payload: { activeGroups: activeLibraryGroups, validate: true } });
    },
    [form, activeLibraryGroups],
  );

  // Stable updater to modify a single row and persist into the form values.
  // This allows cell renderers to update the authoritative form state directly
  // without relying on AG Grid events.
  const updateSampleAt = useCallback(
    (rowIndex: number, patch: Partial<InstrumentRunDataRow>) => {
      // Mark editing in progress
      isUserEditingRef.current = true;

      const current = rowDataRef.current || [];
      const updated = current.map((r, i) => (i === rowIndex ? { ...r, ...patch } : r));

      // Update reducer state so the table reflects the change immediately
      dispatch({ type: 'UPDATE_ROW', payload: { rowIndex, patch } });

      // Clear this row's backend error in the parent too: it re-pushes serverErrors on every
      // change, which would otherwise revive the highlight the user just edited away.
      onSampleRowEdit?.(rowIndex);

      // Update the form values (authoritative source)
      form.setFieldValue?.('samples', updated);

      // Store expected edited samples for sync logic
      expectedEditedSamplesRef.current = updated;

      // An edit is exactly the "user has touched this" signal showCellErrors exists to wait for,
      // so always re-validate here regardless of that gate.
      dispatch({ type: 'SET_LIBRARY_GROUPS', payload: { activeGroups: activeLibraryGroups, validate: true } });
    },
    [form, onSampleRowEdit, activeLibraryGroups],
  );

  // Applies a full-array bulk update — used by bulk operations (apply-to-all, copy-to-rows)
  // where computing all changes at once avoids the stale-closure problem of looping updateSampleAt.
  const handleBulkApply = useCallback(
    (updatedRows: InstrumentRunDataRow[]) => {
      isUserEditingRef.current = true;
      dispatch({ type: 'SYNC_FROM_FORM', payload: { rowData: updatedRows } });
      // A bulk apply rewrites every row, so any backend highlight is now stale. SYNC_FROM_FORM
      // doesn't prune cellErrors, so clear them here and have the parent drop its copy too.
      dispatch({ type: 'CLEAR_CELL_ERRORS' });
      onSampleRowsBulkEdit?.();
      rowDataRef.current = updatedRows;
      form.setFieldValue?.('samples', updatedRows);
      expectedEditedSamplesRef.current = updatedRows;
    },
    [form, onSampleRowsBulkEdit],
  );

  // Effect: Clear isUserEditingRef when form samples match expected after edit
  useEffect(() => {
    const expected = expectedEditedSamplesRef.current;
    if (isUserEditingRef.current && expected && areSamplesEqual(form.state?.values?.samples, expected)) {
      isUserEditingRef.current = false;
      expectedEditedSamplesRef.current = null;
    }
  }, [form.state?.values?.samples]);

  // Clear isResizingRef once form samples match after a resize; otherwise it stays true forever
  // and permanently blocks the form-sync effect, so later sample-count changes do nothing.
  useEffect(() => {
    const expected = expectedResizedSamplesRef.current;
    if (isResizingRef.current && expected && areSamplesEqual(form.state?.values?.samples, expected)) {
      isResizingRef.current = false;
      expectedResizedSamplesRef.current = null;
    }
  }, [form.state?.values?.samples]);

  return (
    <Stack fullWidth gap={4} orientation="vertical">
      {/* Same heading key as the read-only view (RunControlsAndSamples) so the title survives Edit. */}
      <Text as="h3">{tHeader('samples')}</Text>
      <Stack alignItems="center" fullWidth gap={2} justifyContent="space-between" orientation="horizontal">
        <Text className={runStyles.sampleTableDesc} size="xs">
          {t('table_description')}
        </Text>
      </Stack>

      {state.libraryGroupWarning && (
        <Stack alignItems="center" fullWidth gap={2} justifyContent="center" orientation="horizontal">
          <Text as="h3" data-testid="library-group-warning">
            {state.libraryGroupWarning}
          </Text>
        </Stack>
      )}

      <SampleTable
        activeLibraryGroups={activeLibraryGroups}
        allRowData={state.rowData}
        cellErrors={state.cellErrors}
        gridRef={gridRef}
        readOnlyColumns={readOnlyColumns}
        rowData={state.rowData}
        showCellErrors={showCellErrors}
        showCopyToRows={showCopyToRows}
        updateSampleAt={updateSampleAt}
        onBulkApply={handleBulkApply}
        onRowDataChange={handleRowDataChange}
      />
    </Stack>
  );
};

export default SampleManager;
